import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ContentCard from "./ContentCard";
import ViewModal from "./ViewModal";
import NotesPanel from "./NotesPanel";
import BoardSelector from "./BoardSelector";
import { Trash2, ArrowLeft } from "lucide-react";

const BoardView = ({ boardId, onRefresh, categories }) => {
  const { api } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [notesItem, setNotesItem] = useState(null);
  const [boardItem, setBoardItem] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/boards/${boardId}`)
      .then((res) => setBoard(res.data))
      .finally(() => setLoading(false));
  }, [api, boardId]);

  const removeFromBoard = async (contentId) => {
    try {
      await api.delete(`/boards/${boardId}/content/${contentId}`);
      setBoard((prev) => ({
        ...prev,
        content: prev.content.filter((c) => c.id !== contentId),
        content_ids: prev.content_ids.filter((id) => id !== contentId),
      }));
      onRefresh?.();
    } catch {}
  };

  const handleCopyLink = (url) => navigator.clipboard.writeText(url || "");

  if (loading) return (
    <div className="p-8">
      <div className="h-6 w-48 bg-white/[0.05] rounded animate-pulse mb-4" />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="rounded-xl bg-white/[0.03] border border-white/[0.05] h-64 animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-xl font-bold text-white tracking-tight">{board?.name}</h1>
        <p className="text-[#444] text-[13px] mt-0.5">
          {board?.content?.length || 0} items{board?.description ? ` · ${board.description}` : ""}
        </p>
      </div>

      {board?.content?.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-[#444] text-sm">This board is empty. Add content from your library.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {board?.content?.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              onView={() => setViewItem(item)}
              onNotes={() => setNotesItem(item)}
              onBoard={() => setBoardItem(item)}
              onCopyLink={() => handleCopyLink(item.source_url)}
              onDelete={() => removeFromBoard(item.id)}
              categoryColor={categories?.find(c => c.id === item.category_id)?.color}
            />
          ))}
        </div>
      )}

      {viewItem && <ViewModal item={viewItem} onClose={() => setViewItem(null)} />}
      {notesItem && <NotesPanel item={notesItem} onClose={() => setNotesItem(null)} />}
      {boardItem && <BoardSelector item={boardItem} onClose={() => setBoardItem(null)} onDone={onRefresh} />}
    </div>
  );
};

export default BoardView;
